import React, { useState } from "react";
import FilterBar, { computeRange, useSharedFilter } from "../FilterBar";
import { buildParams } from "../api";
import { useAdminData } from "../useAdminData";
import { Panel, Skeleton, Badge, EmptyState, ErrorState, Button, PageHeader, fmtNum, fmtPct, fmtDate } from "../Primitives";
import { PROVIDER_NAMES, platformLabel } from "../catalog";
import { ChevronLeft, ChevronRight } from "lucide-react";

const defaultFilter = () => ({ preset: "30d", ...computeRange("30d"), platform: "", provider: "", game: "", campaign: "" });

function Correlation({ filter }) {
  const [win, setWin] = useState(30);
  const { data, loading, error } = useAdminData(
    async (api) => (await api.get("/admin/signals/correlation", { params: { ...buildParams(filter), window_minutes: win } })).data, [filter, win]);
  return (
    <Panel title="Tipo de sinal × interação posterior" className="mb-4" testid="signal-correlation"
      action={
        <div className="ops-seg" role="group" aria-label="Janela">
          {[10, 30, 60].map((w) => <button key={w} aria-pressed={win === w} onClick={() => setWin(w)} data-testid={`corr-window-${w}`}>{w} min</button>)}
        </div>}>
      {error ? <p className="ops-help px-4 pb-3">{error}</p>
        : loading && !data ? <div className="p-4"><Skeleton className="h-20" /></div>
        : !data.rows.length ? <EmptyState title="Sem visualizações de sinal no período" />
        : (
          <table className="ops-table" data-testid="correlation-table">
            <thead><tr><th>Tipo de sinal</th><th className="!text-right">Visualizações</th><th className="!text-right">Seguidas de clique de saída</th><th className="!text-right">Seguidas de qualquer interação</th><th /></tr></thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.signal_type} className="mia-row">
                  <td className="mono">{r.signal_type}</td>
                  <td className="num">{fmtNum(r.views)}</td>
                  <td className="num">{fmtPct(r.exit_rate)} <span className="text-[var(--muted2)]">({fmtNum(r.with_exit)})</span></td>
                  <td className="num">{fmtPct(r.any_rate)} <span className="text-[var(--muted2)]">({fmtNum(r.with_any)})</span></td>
                  <td className="text-right">{r.small_sample && <Badge tone="warn">amostra &lt; 30</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      {data && <p className="px-4 py-3 ops-help border-t border-[var(--border)]">{data.note}</p>}
    </Panel>
  );
}

export default function Signals() {
  const [filter, setFilter] = useSharedFilter(defaultFilter);
  const [page, setPage] = useState(1);
  const { data, loading, error, reload } = useAdminData(
    async (api) => (await api.get("/admin/signals", { params: { ...buildParams(filter), page, page_size: 25 } })).data, [filter, page]);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const changeFilter = (f) => { setFilter(f); setPage(1); };

  return (
    <div data-testid="signals-page">
      <PageHeader title="Sinais" meta="Metadados das visualizações de sinal. Não há taxa de acerto, lucro ou resultado financeiro.">
        {data && <span className="mono text-[13px] text-[var(--muted)]" data-testid="signals-total">{fmtNum(data.total)} registros</span>}
      </PageHeader>
      <FilterBar value={filter} onChange={changeFilter} show={{ device: false, game: true, campaign: true }} />
      {error && <ErrorState message={error} onRetry={reload} />}
      <Correlation filter={filter} />
      <Panel title="Registros">
        {loading && !data ? <div className="p-4 space-y-2">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-8" />)}</div>
          : !data || data.rows.length === 0 ? <EmptyState title="Nenhum sinal registrado" description="Os registros aparecem quando visitantes visualizam sinais no site." />
          : (
            <>
              <div className="overflow-x-auto mia-scroll">
                <table className="ops-table ops-stack" data-testid="signals-table">
                  <thead><tr><th>Data / hora</th><th>Jogo</th><th>Provedor</th><th>Plataforma</th><th>Tipo de sinal</th></tr></thead>
                  <tbody>
                    {data.rows.map((r, i) => (
                      <tr key={r.event_id || i} className="mia-row">
                        <td className="mono text-[var(--muted)] whitespace-nowrap">{fmtDate(r.server_ts)}</td>
                        <td>{r.game_name || r.game_id || "—"}</td>
                        <td className="text-[var(--muted)]">{PROVIDER_NAMES[r.provider] || r.provider || "—"}</td>
                        <td className="text-[var(--muted)]">{r.platform ? platformLabel(r.platform) : "—"}</td>
                        <td><Badge tone="accent">{r.signal_type || "—"}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination page={page} totalPages={totalPages} onPage={setPage} />
            </>
          )}
      </Panel>
    </div>
  );
}

export function Pagination({ page, totalPages, onPage }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-t border-[var(--border)]">
      <span className="text-[12px] text-[var(--muted)]">Página <span className="mono">{page}</span> de <span className="mono">{totalPages}</span></span>
      <div className="flex gap-1">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="page-prev" aria-label="Página anterior"><ChevronLeft size={14} /></Button>
        <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onPage(page + 1)} data-testid="page-next" aria-label="Próxima página"><ChevronRight size={14} /></Button>
      </div>
    </div>
  );
}
